/**
 * ContractorOpportunitiesPanel — P3 contractor-facing opportunity surface.
 *
 * P4 additions (display only):
 *   • Mode-specific banner copy from contractorMessaging.OPPORTUNITY_BANNER
 *   • Passive inline notices for recently-closed interests (offer_sent,
 *     dispatcher_dismissed, job_left_dispatch). No toasts from sweep events.
 *   • mode === "none" hides the empty list and shows the banner only.
 *   • Action toasts (request sent / duplicate) live inside
 *     useContractorInterest, NOT here.
 *
 * Reads ONLY from getVisibleOpportunities() — never raw `jobs`.
 * Shows payoutAmount only — never customer revenue or internal pricing.
 */

import { useMemo, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Sparkles, MapPin, Calendar, DollarSign, Info, Eye } from "lucide-react";
import { ScopePreviewSheet } from "@/components/contractor/ScopePreviewSheet";
import { useAppData } from "@/data/AppDataContext";
import { useCurrentContractor } from "@/hooks/useCurrentContractor";
import { useContractorInterest } from "@/hooks/useContractorInterest";
import { getVisibleOpportunities } from "@/data/contractor/opportunityVisibility";
import { getContractorOfferMetrics } from "@/data/contractor/offerMetrics";
import { computeScorecard } from "@/hooks/useContractorScorecards";
import {
  OPPORTUNITY_BANNER,
  OPPORTUNITY_EMPTY_LIMITED,
  INTEREST_INLINE_NOTICE,
} from "@/data/contractor/contractorMessaging";

export function ContractorOpportunitiesPanel() {
  const {
    jobs,
    marketplaceOffers,
    contractors,
    opportunities,
    issues,
    credits,
    payProfiles,
    contractorInterests,
    systemEvents,
  } = useAppData();
  const { matchKey } = useCurrentContractor();
  const { submit } = useContractorInterest();

  const contractor = useMemo(() => {
    if (!matchKey) return null;
    return (
      contractors.find(
        (c) =>
          c.id === matchKey ||
          c.companyName.toLowerCase() === matchKey.toLowerCase(),
      ) ?? null
    );
  }, [contractors, matchKey]);

  const resolution = useMemo(() => {
    if (!contractor) return null;
    const metrics = getContractorOfferMetrics(
      contractor.id,
      marketplaceOffers,
      jobs,
      systemEvents,
    );
    const scorecard = computeScorecard(
      contractor,
      opportunities,
      jobs,
      issues,
      credits,
      marketplaceOffers,
    );
    return getVisibleOpportunities(
      contractor,
      jobs,
      marketplaceOffers,
      metrics,
      scorecard,
      { payProfiles },
    );
  }, [
    contractor,
    jobs,
    marketplaceOffers,
    opportunities,
    issues,
    credits,
    payProfiles,
    systemEvents,
  ]);

  if (!contractor || !resolution) return null;

  // Open interests for this contractor → know which cards to mark "Requested".
  const myOpenInterestJobIds = new Set(
    contractorInterests
      .filter((i) => i.contractorId === contractor.id && i.status === "open")
      .map((i) => i.jobId),
  );

  // P4: Recently-closed interests (last 14 days) so contractor sees the
  // outcome of their request without a toast firing from a sweep effect.
  const recentlyClosed = useMemo(() => {
    const cutoff = Date.now() - 14 * 24 * 60 * 60 * 1000;
    return contractorInterests.filter(
      (i) =>
        i.contractorId === contractor.id &&
        i.status !== "open" &&
        i.closeReason &&
        i.closedAt &&
        new Date(i.closedAt).getTime() >= cutoff,
    );
  }, [contractorInterests, contractor.id]);

  const [previewJobId, setPreviewJobId] = useState<string | null>(null);

  const handleRequest = (jobId: string) => {
    // Toasts handled inside useContractorInterest.
    submit(jobId, contractor.id, contractor.id);
  };

  const banner =
    resolution.mode === "limited_exposure"
      ? OPPORTUNITY_BANNER.limited
      : resolution.mode === "offer_only"
        ? OPPORTUNITY_BANNER.offerOnly
        : OPPORTUNITY_BANNER.none;

  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-xs font-bold text-muted-foreground uppercase tracking-[0.12em] flex items-center gap-2">
            <Sparkles className="h-4 w-4" /> Opportunities
          </h2>
          <Badge variant="outline" className="text-[10px] capitalize">
            {resolution.mode.replace("_", " ")}
          </Badge>
        </div>

        {/* Visibility banner — always rendered */}
        <div
          data-testid="opportunity-banner"
          data-mode={resolution.mode}
          className="rounded-md bg-muted/30 border border-border/50 px-3 py-2 mb-3"
        >
          <p className="text-[11px] font-semibold text-foreground">
            {banner.title}
          </p>
          <p className="text-[11px] text-muted-foreground mt-0.5">
            {banner.body}
          </p>
        </div>

        {/* Recently-closed interest inline notices (passive) */}
        {recentlyClosed.length > 0 && (
          <ul
            data-testid="closed-interest-notices"
            className="space-y-1 mb-3"
          >
            {recentlyClosed.map((i) => (
              <li
                key={i.id}
                data-close-reason={i.closeReason}
                className="flex items-start gap-1.5 rounded-md border border-border/40 bg-background/60 px-2 py-1.5 text-[11px] text-muted-foreground"
              >
                <Info className="h-3 w-3 mt-0.5 shrink-0" />
                <span>
                  <span className="font-mono text-[10px] mr-1">{i.jobId.slice(0, 8)}</span>
                  {INTEREST_INLINE_NOTICE[i.closeReason!]}
                </span>
              </li>
            ))}
          </ul>
        )}

        {/* mode === "none" → banner only, no list */}
        {resolution.mode === "none" && null}

        {resolution.mode === "offer_only" && null}

        {resolution.mode === "limited_exposure" &&
          resolution.opportunities.length === 0 && (
            <p className="text-xs text-muted-foreground">
              {OPPORTUNITY_EMPTY_LIMITED}
            </p>
          )}

        {resolution.mode === "limited_exposure" &&
          resolution.opportunities.length > 0 && (
            <ul className="space-y-2">
              {resolution.opportunities.map((opp) => {
                const requested = myOpenInterestJobIds.has(opp.jobId);
                return (
                  <li
                    key={opp.jobId}
                    className="rounded-lg border border-border/60 p-3 bg-muted/10"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-semibold truncate">
                          {opp.projectType}
                        </p>
                        <div className="flex items-center gap-1 text-[11px] text-muted-foreground mt-0.5">
                          <MapPin className="h-3 w-3" />
                          <span className="truncate">
                            {opp.city}
                            {opp.zip ? ` · ${opp.zip}` : ""}
                          </span>
                        </div>
                        {opp.scheduledDate && (
                          <div className="flex items-center gap-1 text-[11px] text-muted-foreground mt-0.5">
                            <Calendar className="h-3 w-3" />
                            <span>{opp.scheduledDate}</span>
                          </div>
                        )}
                        <div className="flex items-center gap-1 text-[11px] font-semibold text-foreground mt-1">
                          <DollarSign className="h-3 w-3" />
                          <span>
                            ${opp.payoutAmount.toLocaleString()} payout
                          </span>
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant={requested ? "outline" : "default"}
                        className="h-7 text-[11px] shrink-0"
                        onClick={() => setPreviewJobId(opp.jobId)}
                      >
                        <Eye className="h-3 w-3 mr-1" />
                        {requested ? "Requested" : "Preview"}
                      </Button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
      </CardContent>
      <ScopePreviewSheet
        open={previewJobId !== null}
        onOpenChange={(o) => {
          if (!o) setPreviewJobId(null);
        }}
        jobId={previewJobId}
        payoutAmount={
          previewJobId
            ? resolution.opportunities.find((o) => o.jobId === previewJobId)
                ?.payoutAmount
            : undefined
        }
        alreadyRequested={
          previewJobId ? myOpenInterestJobIds.has(previewJobId) : false
        }
        onRequest={() => previewJobId && handleRequest(previewJobId)}
      />
    </Card>
  );
}
